/**
 * Gestión de cuota y persistencia del almacenamiento del navegador.
 *
 * IndexedDB puede ser expulsada por el navegador (modo "evictable")
 * cuando el dispositivo está bajo presión de espacio, salvo que se
 * haya pedido almacenamiento persistente. Además, las escrituras
 * fallan con QuotaExceededError cuando se llena; este módulo ofrece
 * helpers para detectar y manejar ambos casos de forma graceful.
 */

/**
 * Error tipado para fallos de cuota. Permite a los stores distinguir
 * "sin espacio" de otros errores y mostrar un mensaje útil al usuario.
 */
export class StorageQuotaError extends Error {
  constructor(message = 'Sin espacio de almacenamiento. Libera libros antiguos.') {
    super(message)
    this.name = 'StorageQuotaError'
  }
}

/** Devuelve true si el error parece ser de cuota (Dexie/navegador). */
export function isQuotaError(e: unknown): boolean {
  // Nuestro propio error tipado (ya clasificado).
  if (e instanceof StorageQuotaError) return true
  if (e instanceof DOMException && (e.name === 'QuotaExceededError' || e.code === 22)) {
    return true
  }
  const name = (e as { name?: string })?.name ?? ''
  const msg = e instanceof Error ? e.message : String(e)
  return name === 'QuotaExceededError' || /quota|exceeded|full/i.test(msg)
}

/**
 * Pide al navegador almacenamiento persistente (no evictable).
 * Es un no-op si la API no está disponible o el usuario ya lo concedió.
 * Devuelve true si el almacenamiento quedó persistente.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (navigator.storage?.persist) {
      if (await navigator.storage.persisted()) return true
      return await navigator.storage.persist()
    }
  } catch (e) {
    console.warn('[storage] no se pudo pedir persistencia:', e)
  }
  return false
}

/** ¿Está el almacenamiento marcado como persistente? */
export async function isStoragePersistent(): Promise<boolean> {
  try {
    return Boolean(await navigator.storage?.persisted?.())
  } catch {
    return false
  }
}

export interface StorageEstimate {
  usage: number
  quota: number
}

/**
 * Estima el uso y la cuota disponibles. Devuelve null si la API
 * no está disponible ( navegadores antiguos / contextos sin permiso).
 */
export async function getStorageEstimate(): Promise<StorageEstimate | null> {
  try {
    const est = await navigator.storage?.estimate?.()
    if (!est) return null
    return { usage: est.usage ?? 0, quota: est.quota ?? 0 }
  } catch {
    return null
  }
}

/**
 * Comprueba si hay espacio suficiente para `neededBytes` adicionales.
 * Devuelve true si hay margen (o si no se puede estimar, por optimismo).
 */
export async function hasStorageSpace(neededBytes: number): Promise<boolean> {
  const est = await getStorageEstimate()
  if (!est || est.quota === 0) return true // no estimable: no bloquear.
  return est.usage + neededBytes < est.quota
}
